import {
    AUTH_TOKEN_ERROR_MESSAGES,
    PHONE_NUMBER_REGEX,
    TAX_NUMBER_REGEX,
} from "@/core/constants/const";
import {
    IDinhMucCoDinhResponseBase,
    IVatTuDinhMucCoDinhResponseBase,
    IVatTuHopDongResponseBase,
    IVatTuPhieuNhapKho,
    IVatTuResponseBase,
    IVatTuResponseBaseWithApprovedStatus,
    IVatTuSearchInKhoResponseBase,
    IVatTuTrongKhoResponseBase,
    IVatTuWithSoLuongDaXuat,
    MessageItemResponse,
} from "@/core/interfaces/ApiResponses";
import ApiService from "@/core/services/ApiService";
import Swal from "sweetalert2";
import { IValidationRule } from "../interfaces/Models";
import { PhieuNhapXuatStatusEnum } from "./../enums/phieuNhapXuatStatus.enum";

import {
    getIdDonViFromUserSettings,
    getYearFromUserSettings,
} from "@/core/helpers/userSystemSettings";

const YEAR = "selected_year" as string;

const HOI_DONG_BBKN = "hoi_dong_bbkn" as string;

export type HoiDongBbknMember = {
  thuTu: number;
  hoTen: string;
  chucVu: string;
  thanhPhan: string;
};

export const getYearFromLocalStorage = (): string | null => {
  return getYearFromUserSettings();
};

export const setSelectedYearToLocalStorage = (year: string) => {
  window.localStorage.setItem(YEAR, year);
};

export const removeSelectedYearFromLocalStorage = () => {
  window.localStorage.removeItem(YEAR);
};

/**
 * Persist hội đồng BBKN for the next create form.
 * Accepts either a JSON string or the member array (avoids `[object Object]` corruption).
 */
export const setHoiDongBbkn = (data: HoiDongBbknMember[] | string) => {
  const value = typeof data === "string" ? data : JSON.stringify(data ?? []);

  window.localStorage.setItem(HOI_DONG_BBKN, value);
};

export const setUserToLocalStorage = (data: unknown) => {
  window.localStorage.setItem("current_user", JSON.stringify(data));
};

export const getUserFromLocalStorage = () => {
  const userData = window.localStorage.getItem("current_user");

  if (!userData) return null;

  return JSON.parse(userData as string);
};

export const clearUserFromLocalStorage = () => {
  window.localStorage.removeItem("current_user");
};

/** Normalize `thanh_phan_hoi_dong` from API / localStorage (string or already-parsed array). */
export const parseThanhPhanHoiDong = (
  value: unknown
): { members: HoiDongBbknMember[]; invalid: boolean } => {
  if (value == null || value === "") {
    return { members: [], invalid: false };
  }

  if (Array.isArray(value)) {
    return { members: value as HoiDongBbknMember[], invalid: false };
  }

  if (typeof value !== "string") {
    return { members: [], invalid: true };
  }

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return { members: [], invalid: true };
    }

    return { members: parsed as HoiDongBbknMember[], invalid: false };
  } catch {
    return { members: [], invalid: true };
  }
};

export const getHoiDongBbkn = (): HoiDongBbknMember[] => {
  try {
    const raw = window.localStorage.getItem(HOI_DONG_BBKN);

    if (raw === null) {
      return [];
    }

    return parseThanhPhanHoiDong(raw).members;
  } catch {
    return [];
  }
};

const PHIEU_NHAP_KHO_FORM_DRAFT = "phieu_nhap_kho_form_draft";

export type PhieuNhapKhoFormDraft = {
  nguoi_giao?: string;
  giam_doc?: string;
  y_kien?: string;
};

/** Restore draft fields for phiếu nhập kho create form. */
export const getPhieuNhapKhoFormDraft = (): PhieuNhapKhoFormDraft => {
  try {
    const raw = window.localStorage.getItem(PHIEU_NHAP_KHO_FORM_DRAFT);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as PhieuNhapKhoFormDraft;

    return {
      nguoi_giao:
        typeof parsed.nguoi_giao === "string" ? parsed.nguoi_giao : "",
      giam_doc: typeof parsed.giam_doc === "string" ? parsed.giam_doc : "",
      y_kien: typeof parsed.y_kien === "string" ? parsed.y_kien : "",
    };
  } catch {
    return {};
  }
};

/** Persist draft fields for the next phiếu nhập kho create form. */
export const setPhieuNhapKhoFormDraft = (data: PhieuNhapKhoFormDraft) => {
  const payload: PhieuNhapKhoFormDraft = {
    nguoi_giao: data.nguoi_giao?.trim() ?? "",
    giam_doc: data.giam_doc?.trim() ?? "",
    y_kien: data.y_kien?.trim() ?? "",
  };

  window.localStorage.setItem(
    PHIEU_NHAP_KHO_FORM_DRAFT,
    JSON.stringify(payload)
  );
};

export const apiServiceInstance = () => ApiService;

export {
    calcLineAmount,
    formatAmount,
    formatQuantity,
    formatUnitPrice,
    NUMBER_FORMAT_LOCALE,
    parseFormattedNumber
} from "./number-format";

import {
    formatAmount,
    formatQuantity,
    NUMBER_FORMAT_LOCALE,
    parseFormattedNumber,
} from "./number-format";

/**
 * @deprecated Use `formatQuantity`, `formatUnitPrice`, or `formatAmount` from `@/core/helpers/number-format`.
 */
export const formatCurrencyAndQuantity = (
  value: number,
  isShowDecimal = true
) => {
  if (isShowDecimal) {
    return formatQuantity(value);
  }

  return formatAmount(value);
};

/** @deprecated Use `parseFormattedNumber` from `@/core/helpers/number-format`. */
export const reverseFormatNumber = (
  val: string,
  locale = NUMBER_FORMAT_LOCALE
) => parseFormattedNumber(val, locale);

export function isResponseError<T>(
  data: MessageItemResponse | T
): data is MessageItemResponse {
  return !!(data as MessageItemResponse).msg?.trim();
}

export const swalErrNotification = async (
  error,
  defaultErrMsg = "Có lỗi xảy ra"
) => {
  if (!error) {
    await swalNotification(defaultErrMsg, "error");

    return;
  }

  const errorMsg = getErrorMsg(error, defaultErrMsg);
  if (
    AUTH_TOKEN_ERROR_MESSAGES.some(
      (tokenError) =>
        tokenError.trim().toLowerCase() === errorMsg.trim().toLowerCase()
    )
  ) {
    await swalNotification("Bạn phải đăng nhập để sử dụng", "info");

    return;
  }

  await swalNotification(getErrorMsg(error, defaultErrMsg), "error");
};

export const swalNotification = async (
  textMessage: string,
  type: "error" | "success" | "warning" | "info" = "success",
  confirmButtonText = "Đã hiểu"
) => {
  let buttonType;

  switch (type) {
    case "error":
      buttonType = "danger";
      break;
    case "info":
      buttonType = "info";
      break;
    default:
      buttonType = "primary";
      break;
  }
  await Swal.fire({
    text: textMessage,
    icon: type,
    buttonsStyling: false,
    allowOutsideClick: false,
    confirmButtonText: confirmButtonText,
    focusCancel: true,
    focusConfirm: true,
    customClass: {
      confirmButton: `btn btn-${buttonType}`,
    },
  });
};

export type SwalConfirmOptions = {
  title?: string;
  text?: string;
  html?: string;
  icon?: "warning" | "question" | "info" | "error" | "success";
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonClass?: string;
  cancelButtonClass?: string;
};

/**
 * Shared confirm dialog via SweetAlert2.
 * Returns true when the user confirms, false on cancel/dismiss.
 */
export const swalConfirm = async (
  options: SwalConfirmOptions | string = {}
): Promise<boolean> => {
  const opts: SwalConfirmOptions =
    typeof options === "string" ? { text: options } : options;

  const result = await Swal.fire({
    title: opts.title ?? "Xác nhận",
    text: opts.html ? undefined : opts.text,
    html: opts.html,
    icon: opts.icon ?? "question",
    showCancelButton: true,
    buttonsStyling: false,
    allowOutsideClick: false,
    reverseButtons: true,
    focusCancel: true,
    confirmButtonText: opts.confirmButtonText ?? "Đồng ý",
    cancelButtonText: opts.cancelButtonText ?? "Hủy bỏ",
    customClass: {
      confirmButton: opts.confirmButtonClass ?? "btn btn-primary",
      cancelButton: opts.cancelButtonClass ?? "btn btn-light",
    },
  });

  return result.isConfirmed;
};

export const getErrorMsg = (error, defaultError = "Có lỗi xảy ra"): string => {
  return error.response?.data?.data?.msg || defaultError;
};

export const instanceOfIDinhMucCoDinhResponseBase = (
  obj: unknown
): obj is IDinhMucCoDinhResponseBase => {
  try {
    return typeof obj === "object" && obj !== null && "pivot" in obj;
  } catch (e) {
    console.log("not an instance of IDinhMucCoDinhResponseBase");

    return false;
  }
};

export const instanceOfIVatTuHopDongResponseBase = (
  obj: unknown
): obj is IVatTuHopDongResponseBase => {
  try {
    return (
      typeof obj === "object" &&
      obj !== null &&
      "so_luong_trong_hop_dong" in obj
    );
  } catch {
    console.log("not an instance of IVatTuHopDongResponseBase");

    return false;
  }
};

export const instanceOfIVatTuPhieuNhapKhoResponseBase = (
  obj: unknown
): obj is IVatTuPhieuNhapKho => {
  try {
    return (
      typeof obj === "object" &&
      obj !== null &&
      "pivot" in obj &&
      "id_phieu_nhap_kho" in (obj as { pivot: Record<string, unknown> }).pivot
    );
  } catch {
    console.log("not an instance of IVatTuPhieuNhapKho");

    return false;
  }
};

export const instanceOfIVatTuTrongKhoResponseBase = (
  obj: unknown
): obj is IVatTuTrongKhoResponseBase => {
  try {
    return (
      typeof obj === "object" &&
      obj !== null &&
      "pivot" in obj &&
      "id_kho" in (obj as { pivot: Record<string, unknown> }).pivot
    );
  } catch {
    console.log("not an instance of IVatTuTrongKhoResponseBase");

    return false;
  }
};

export function strip(number) {
  return (number * 1000) / 1000;
}

export const instanceOfIVatTuResponseBaseWithApprovedStatus = (
  obj: unknown
): obj is IVatTuResponseBaseWithApprovedStatus => {
  try {
    return typeof obj === "object" && obj !== null && "is_approved" in obj;
  } catch {
    return false;
  }
};

export const instanceOfIVatTuWithSoLuongDaXuat = (
  obj: unknown
): obj is IVatTuWithSoLuongDaXuat => {
  try {
    return (
      typeof obj === "object" && obj !== null && "so_luong_da_xuat_kho" in obj
    );
  } catch {
    return false;
  }
};

export const instanceOfIVatTuSearchInKhoResponseBase = (
  obj: unknown
): obj is IVatTuSearchInKhoResponseBase => {
  try {
    return typeof obj === "object" && obj !== null && "kho" in obj;
  } catch {
    return false;
  }
};

export const instanceOfIVatTuDinhMucCoDinhResponseBase = (
  obj: unknown
): obj is IVatTuDinhMucCoDinhResponseBase => {
  try {
    return (
      typeof obj === "object" && obj !== null && "so_luong_dinh_muc" in obj
    );
  } catch {
    return false;
  }
};

export const transformFirstLetterToUpperCase = (str: string): string => {
  if (!str) return str;

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const instanceOfIVatTuResponseBase = (
  obj: unknown
): obj is IVatTuResponseBase => {
  try {
    return (
      typeof obj === "object" &&
      obj !== null &&
      "id" in obj &&
      "ten_vat_tu" in obj
    );
  } catch {
    return false;
  }
};

export const getIdDonViFromLocalStorage = () => {
  return getIdDonViFromUserSettings();
};

export const getMaDonViFromLocalStorage = () => {
  const userData = window.localStorage.getItem("current_user");

  if (!userData) return null;

  const parseUserData = JSON.parse(userData);

  return parseUserData?.ma_don_vi || null;
};

/**
 * Validates phone number using comprehensive regex pattern
 * Allows formats like: +84 901-234-567, (028) 1234-5678, 0901234567
 * @param phoneNumber - The phone number to validate
 * @returns boolean - true if valid, false otherwise
 */
export const validatePhoneNumber = (
  phoneNumber: string | null | undefined
): boolean => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return true; // Allow empty/null values
  }

  return PHONE_NUMBER_REGEX.test(phoneNumber.trim());
};

/**
 * Validates tax number allowing 7, 10, 12, or 14 characters
 * If 14 characters, the 11th character must be a dash (-)
 * @param taxNumber - The tax number to validate
 * @returns boolean - true if valid, false otherwise
 */
export const validateTaxNumber = (
  taxNumber: string | null | undefined
): boolean => {
  if (!taxNumber || taxNumber.trim() === "") {
    return true; // Allow nullable values
  }

  const trimmedTaxNumber = taxNumber.trim();

  // Check if it matches the basic regex pattern
  if (!TAX_NUMBER_REGEX.test(trimmedTaxNumber)) {
    return false;
  }

  // Additional validation for 14-character tax numbers
  // Ensure the 11th character (index 10) is a dash
  if (trimmedTaxNumber.length === 14 && trimmedTaxNumber.charAt(10) !== "-") {
    return false;
  }

  return true;
};

/**
 * Formats contract number to uppercase and allows spaces
 * @param contractNumber - The contract number to format
 * @returns string - Formatted contract number
 */
export const formatContractNumber = (contractNumber: string): string => {
  if (!contractNumber) return "";

  // Convert to uppercase and preserve spaces, max 20 characters
  return contractNumber.toUpperCase().slice(0, 20);
};

export const mapPhieuNhapXuatStatusToLabel = (
  status: PhieuNhapXuatStatusEnum
): string => {
  switch (status) {
    case PhieuNhapXuatStatusEnum.PENDING:
      return "Đang chờ xử lý";
    case PhieuNhapXuatStatusEnum.FAILED:
      return "Thất bại";
    case PhieuNhapXuatStatusEnum.SUCCESS:
      return "Thành công";
    case PhieuNhapXuatStatusEnum.PROCESSING:
      return "Đang xử lý";
    default:
      return "Không xác định";
  }
};

export const mapPhieuNhapXuatStatusToLevelColor = (
  status: PhieuNhapXuatStatusEnum
): string => {
  switch (status) {
    case PhieuNhapXuatStatusEnum.PENDING:
      return "warning";
    case PhieuNhapXuatStatusEnum.FAILED:
      return "danger";
    case PhieuNhapXuatStatusEnum.SUCCESS:
      return "success";
    case PhieuNhapXuatStatusEnum.PROCESSING:
    default:
      return "info";
  }
};

// pending -> enable
// failed -> enable
// processing -> disabled
// success -> hide
export const getDuyetPhieuNhapXuatStatus = (
  status: PhieuNhapXuatStatusEnum
): {
  isDisplay: boolean;
  isDisabled: boolean;
} => {
  switch (status) {
    case PhieuNhapXuatStatusEnum.PENDING:
      return {
        isDisplay: true,
        isDisabled: false,
      };
    case PhieuNhapXuatStatusEnum.FAILED:
      return {
        isDisplay: true,
        isDisabled: false,
      };
    case PhieuNhapXuatStatusEnum.PROCESSING:
      return {
        isDisplay: true,
        isDisabled: true,
      };
    case PhieuNhapXuatStatusEnum.SUCCESS:
      return {
        isDisplay: false,
        isDisabled: false,
      };
    default:
      return {
        isDisplay: false,
        isDisabled: false,
      };
  }
};

export const mapLoaiPhieuXuatToLabel = (loai_phieu: number): string => {
  switch (loai_phieu) {
    case 1:
      return "Phiếu xuất khác ";
    case 2:
      return "Phiếu thu hồi vật tư";
    case 0:
    default:
      return "Phiếu xuất theo BBGT";
  }
};

export const lengthValidationRules = ({
  min = 5,
  max = 60,
  name,
  isRequired = true,
}: {
  min?: number;
  max?: number;
  name?: string;
  isRequired?: boolean;
} = {}): IValidationRule[] => {
  const rules: IValidationRule[] = [];

  if (isRequired) {
    rules.push({
      required: true,
      message: `${name || "Trường này"} không được để trống`,
      trigger: "change",
    });
  }

  rules.push({
    min,
    max,
    message: `${
      name || "Trường này"
    } phải có độ dài từ ${min} đến ${max} ký tự`,
    trigger: "change",
  });

  return rules;
};

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
};

interface SaveFilePickerWritable {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

interface SaveFilePickerHandle {
  createWritable: () => Promise<SaveFilePickerWritable>;
}

export const downloadBase64File = async (
  fileData: string,
  mimeType: string,
  filename: string
) => {
  const blob = base64ToBlob(fileData, mimeType);
  const extension = filename.includes(".")
    ? `.${filename.split(".").pop()}`
    : "";

  const showSaveFilePicker = (
    window as Window & {
      showSaveFilePicker?: (options?: {
        suggestedName?: string;
        types?: Array<{
          description: string;
          accept: Record<string, string[]>;
        }>;
      }) => Promise<SaveFilePickerHandle>;
    }
  ).showSaveFilePicker;

  if (showSaveFilePicker) {
    try {
      const handle = await showSaveFilePicker({
        suggestedName: filename,
        ...(extension
          ? {
              types: [
                {
                  description: "File",
                  accept: { [mimeType]: [extension] },
                },
              ],
            }
          : {}),
      });
      const writable = await handle.createWritable();

      await writable.write(blob);
      await writable.close();

      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
